import { gql } from '@apollo/client';

import { isArchivedChatsField } from './bumpChatToTop';

import type { ApolloCache } from '@apollo/client';
import type {
  ArchiveChatMutation,
  UnarchiveChatMutation,
} from '@graphql/generated';

/**
 * TA-10878: archiving a chat left its row sitting in the active chat list.
 *
 * Every archive/unarchive call site passes `refetchQueries` in the mutate
 * options, which REPLACES the hook-level `refetchQueries` rather than adding
 * to it — and each one lists only `{ archivesOnly: true }`. So the archived
 * list refetches and the active list never does. The row then survives until
 * something unrelated refetches page 1; on web nothing does while the user
 * stays on the chats screen (the only active-list refetch is the
 * `window.focus` listener), so the archived chat stays clickable in the list
 * until a full reload.
 *
 * Refetching the active list would fix it over the network, at the cost of a
 * round trip and a visible reshuffle. The cache already knows everything
 * needed: drop the chat's edge out of the connection it just left. The
 * connection is stored per `keyArgs: ['archivesOnly']`, so the active and
 * archived lists are two separate store fields — `isArchivedChatsField` tells
 * them apart from `storeFieldName`, the same way bumpChatToTop does.
 */
const chatListKindMatches = (
  storeFieldName: string | undefined,
  list: ChatListKind
): boolean => isArchivedChatsField(storeFieldName) === (list === 'archived');

export type ChatListKind = 'active' | 'archived';

/**
 * Removes a chat's edge from one of the two cached `listChatsPagination`
 * connections. Returns true when an edge was actually dropped.
 */
export const removeChatFromChatList = (
  cache: ApolloCache<any>,
  chatId: string,
  list: ChatListKind
): boolean => {
  let removed = false;

  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      listChatsPagination(existingConn: any, details: any) {
        const { readField, storeFieldName } = details;
        if (!existingConn?.edges) return existingConn;
        if (!chatListKindMatches(storeFieldName, list)) return existingConn;

        const edges = existingConn.edges.filter(
          (edge: any) => readField('id', edge?.node) !== chatId
        );
        if (edges.length === existingConn.edges.length) return existingConn;

        removed = true;
        return { ...existingConn, edges };
      },
    },
  });

  return removed;
};

// `archiveChat` returns `settings { archivedAt }` — the nested UserChatSetting
// — but the list row and ChatsData's archived-row filter read the FLAT
// `settingsArchivedAt` field on Chat, which the mutation response never
// touches. writeFragment rather than cache.modify: modify cannot add a field
// that is absent from the store object, and a chat opened from search or a
// deep link has no `settingsArchivedAt` cached at all.
const CHAT_ARCHIVED_AT_FRAGMENT = gql`
  fragment ChatSettingsArchivedAt on Chat {
    settingsArchivedAt
  }
`;

/**
 * True when the cache has this chat marked archived.
 *
 * ChatsData keeps chat rows as state snapshots (page 2+ is fetched
 * `no-cache`, so those objects never update), and its merge deliberately
 * keeps a chat that a page-1 response no longer contains. The entity written
 * by applyChatArchiveState is the only place that knows the row has left the
 * active list.
 */
export const isChatArchivedInCache = (
  cache: ApolloCache<any>,
  chatId?: string
): boolean => {
  if (!chatId) return false;
  const chatCacheId = cache.identify({ __typename: 'Chat', id: chatId });
  if (!chatCacheId) return false;
  return !!cache.readFragment<{ settingsArchivedAt?: string | null }>({
    id: chatCacheId,
    fragment: CHAT_ARCHIVED_AT_FRAGMENT,
  })?.settingsArchivedAt;
};

/**
 * Applies the result of archiving (or unarchiving) a chat to the cache: marks
 * the Chat entity, and takes its row out of the list it no longer belongs to.
 *
 * Pass the `archivedAt` the server returned; `null` means unarchived.
 */
export const applyChatArchiveState = (
  cache: ApolloCache<any>,
  chatId: string,
  archivedAt: string | null
): void => {
  const chatCacheId = cache.identify({ __typename: 'Chat', id: chatId });
  if (!chatCacheId) return;

  cache.writeFragment({
    id: chatCacheId,
    fragment: CHAT_ARCHIVED_AT_FRAGMENT,
    data: { __typename: 'Chat', settingsArchivedAt: archivedAt },
  });

  removeChatFromChatList(cache, chatId, archivedAt ? 'active' : 'archived');
};

/**
 * `update` for the archiveChat mutation. Drops the row out of the active list
 * as soon as the server confirms the archive, so the list does not have to
 * wait for a refetch that the call sites do not actually request.
 */
export const archiveChatCacheUpdate = (
  cache: ApolloCache<any>,
  result: { data?: ArchiveChatMutation | null }
): void => {
  const chat = result?.data?.archiveChat;
  if (!chat?.id) return;
  // The server's own timestamp when it sends one; otherwise any non-null
  // value is enough — the field is read as a boolean "is archived" here, and
  // the next list fetch replaces it with the real one.
  applyChatArchiveState(
    cache,
    chat.id,
    chat.settings?.archivedAt ?? new Date().toISOString()
  );
};

/**
 * `update` for the unarchiveChat mutation — the mirror image, and the reason
 * archiveChatCacheUpdate is safe: without it the flat `settingsArchivedAt`
 * written above would stay set on a restored chat and ChatsData would keep
 * filtering its row out of the active list.
 */
export const unarchiveChatCacheUpdate = (
  cache: ApolloCache<any>,
  result: { data?: UnarchiveChatMutation | null }
): void => {
  const chat = result?.data?.unarchiveChat;
  if (!chat?.id) return;
  applyChatArchiveState(cache, chat.id, null);
};
